/**
 * DeductionReportPage — Landlord Portal (JR-5)
 *
 * Deduction-report compiler: matched vs. unverified-candidate buckets, side-by-side
 * move-in / move-out (+ tenant counter-evidence) per line item, editable amount +
 * receipt, a running total, and the generate -> send -> delivery-status -> retry
 * lifecycle.
 *
 * Route: mounted at a lease-scoped path with a `leaseId` param (e.g.
 * `/leases/:leaseId/deduction-report`). BR-20a/DC-8: the page loads read-only via
 * `getDeductionReport(leaseId)` (GET .../deduction-report, never creates a row) — if the
 * lease has no report yet, an empty state offers an explicit "Compile Report" action.
 * `compileDeductionReport` (the idempotent find-or-create/reconcile — see
 * `DeductionReportService.compile()`) only ever runs from that explicit button, both for
 * the first compile and any later recompile while the report is still editable
 * (`status !== 'SENT'`) — never as an on-mount side effect, so simply opening this page
 * (e.g. from an archived lease's read-only record link) can never manufacture state.
 *
 * NOTE (flagged gap, not improvised around): the boundary layer exposes no
 * upload-url endpoint for a line item's receipt — `receiptStorageKey` is only settable
 * as an already-known string via `PATCH .../line-items/:id`, and `condition-s3.client.ts`
 * has no `generateReceiptKey` helper. This page therefore only *displays* an existing
 * receipt (via the authz-gated stream proxy) and cannot itself upload one.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Send, RotateCw, AlertTriangle, Paperclip } from 'lucide-react';
import {
  getDeductionReport,
  compileDeductionReport,
  editDeductionLineItem,
  generateDeductionReport,
  sendDeductionReport,
  retryDeductionReportDelivery,
  deductionReportPdfUrl,
  deductionLineItemReceiptUrl,
  ReportNotReadyClientError,
  DEFICIENCY_TYPE_LABELS,
  type DeductionReportResponse,
  type DeductionLineItemResponse,
  type ReportNotReadyGap,
} from '../lib/api';
import { formatAmount, parseDollarsToCents } from '../utils/expense.utils';
import { PageHeader } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';

function ConditionLabel({ condition }: { condition: DeductionLineItemResponse['moveOut']['condition'] | null }) {
  if (condition === 'OK') return <span className="text-success">OK</span>;
  if (condition === 'DEFICIENT') return <span className="text-destructive">Deficient</span>;
  if (condition === null) return <span className="text-muted-foreground">—</span>;
  return <span className="text-muted-foreground">Not assessed</span>;
}

/** `<span>`-rooted delivery-status renderer — used everywhere a `<p>` is the parent
 * (PageHeader's `subtitleNode`, the "Sent ..." delivery-status-panel line), since
 * `Badge` renders a `<div>`, which is invalid inside `<p>` and trips React's
 * DOM-nesting validation (surfaces as a console.error in every e2e test). */
function DeliveryStatusInline({ status }: { status: DeductionReportResponse['deliveryStatus'] }) {
  if (status === 'SENT') return <span className="font-medium text-success">Delivered</span>;
  if (status === 'FAILED') return <span className="font-medium text-destructive">Delivery Failed</span>;
  return <span className="font-medium text-muted-foreground">Pending</span>;
}

export function DeductionReportPage() {
  const { leaseId } = useParams<{ leaseId: string }>();

  const [report, setReport] = useState<DeductionReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [lineDrafts, setLineDrafts] = useState<Record<string, { amount: string; description: string }>>({});
  const [savingLineId, setSavingLineId] = useState<string | null>(null);

  const [generateWarnings, setGenerateWarnings] = useState<string[] | null>(null);
  const [generateGaps, setGenerateGaps] = useState<ReportNotReadyGap[] | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isCompiling, setIsCompiling] = useState(false);
  const [compileError, setCompileError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const applyReport = (updated: DeductionReportResponse) => {
    setReport(updated);
    setLineDrafts(
      Object.fromEntries(
        updated.lineItems.map((li) => [li.id, { amount: (li.amountCents / 100).toFixed(2), description: li.description }])
      )
    );
  };

  /** BR-20a/DC-8: read-only load — never compiles/creates a report. */
  const loadReport = useCallback(async () => {
    if (!leaseId) return;
    setIsLoading(true);
    setError(null);
    try {
      const { report: existing } = await getDeductionReport(leaseId);
      if (existing) applyReport(existing);
      else setReport(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load deduction report');
    } finally {
      setIsLoading(false);
    }
  }, [leaseId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  /** Explicit compile/recompile action (BR-20a) — the only path that ever calls the
   * idempotent find-or-create/reconcile compiler. Used both for the empty state's first
   * compile and, once a report exists, as an explicit "Recompile" while still editable. */
  const handleCompile = async () => {
    if (!leaseId) return;
    setIsCompiling(true);
    setCompileError(null);
    try {
      const { report: compiled } = await compileDeductionReport(leaseId);
      applyReport(compiled);
    } catch (err) {
      setCompileError(err instanceof Error ? err.message : 'Failed to compile report');
    } finally {
      setIsCompiling(false);
    }
  };

  const matchedItems = useMemo(() => report?.lineItems.filter((li) => li.baselineStatus === 'MATCHED') ?? [], [report]);
  const unverifiedItems = useMemo(() => report?.lineItems.filter((li) => li.baselineStatus === 'UNVERIFIED') ?? [], [report]);
  const isDraftOrGenerated = report ? report.status !== 'SENT' : false;

  const handleSaveLine = async (lineItemId: string) => {
    const draft = lineDrafts[lineItemId];
    if (!draft) return;
    const amountCents = parseDollarsToCents(draft.amount);
    if (amountCents < 0) {
      setActionError('Amount cannot be negative.');
      return;
    }
    setSavingLineId(lineItemId);
    setActionError(null);
    try {
      const { report: updated } = await editDeductionLineItem(lineItemId, {
        amountCents,
        description: draft.description.trim(),
      });
      setReport(updated);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to save line item');
    } finally {
      setSavingLineId(null);
    }
  };

  const handleGenerate = async () => {
    if (!report) return;
    setIsGenerating(true);
    setActionError(null);
    setGenerateWarnings(null);
    setGenerateGaps(null);
    try {
      const { report: updated, warnings } = await generateDeductionReport(report.id);
      setReport(updated);
      setGenerateWarnings(warnings);
    } catch (err) {
      if (err instanceof ReportNotReadyClientError) {
        setGenerateGaps(err.gaps);
      } else {
        setActionError(err instanceof Error ? err.message : 'Failed to generate report');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!report) return;
    setIsSending(true);
    setActionError(null);
    try {
      const { report: updated } = await sendDeductionReport(report.id);
      setReport(updated);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to send report');
    } finally {
      setIsSending(false);
    }
  };

  const handleRetry = async () => {
    if (!report) return;
    setIsRetrying(true);
    setActionError(null);
    try {
      const { report: updated } = await retryDeductionReportDelivery(report.id);
      setReport(updated);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to retry delivery');
    } finally {
      setIsRetrying(false);
    }
  };

  if (!leaseId) {
    return (
      <div className="px-[var(--page-padding-x)] py-8" data-testid="deduction-report-page">
        <p className="text-sm text-destructive" role="alert">Missing lease.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto pb-6" data-testid="deduction-report-page">
        <div className="px-[var(--page-padding-x)] pt-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-24 mt-2" />
        </div>
        <div className="px-[var(--page-padding-x)] mt-4 space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-[var(--page-padding-x)] py-8" data-testid="deduction-report-page">
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="alert" data-testid="error-message">
          <p className="text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={loadReport} className="mt-2">Retry</Button>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="max-w-3xl mx-auto pb-24" data-testid="deduction-report-page">
        <div className="px-[var(--page-padding-x)]">
          <PageHeader
            title="Deduction Report"
            backLink={{ label: 'Lease', to: `/leases/${leaseId}` }}
          />
        </div>
        {compileError && (
          <div className="mx-[var(--page-padding-x)] mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="alert" data-testid="action-error">
            <p className="text-destructive">{compileError}</p>
          </div>
        )}
        <div
          className="mx-[var(--page-padding-x)] rounded-lg border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground text-center flex flex-col items-center gap-3"
          data-testid="deduction-report-empty"
        >
          <span>No deduction report has been compiled for this lease yet.</span>
          <Button onClick={handleCompile} disabled={isCompiling} data-testid="compile-report-button">
            {isCompiling ? 'Compiling…' : 'Compile Report'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto pb-24" data-testid="deduction-report-page">
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Deduction Report"
          subtitleNode={
            <span data-testid="deduction-report-status">
              {report.status} · <DeliveryStatusInline status={report.deliveryStatus} />
            </span>
          }
          backLink={{ label: 'Lease', to: `/leases/${leaseId}` }}
          action={
            report.generatedAt && (
              <a
                href={deductionReportPdfUrl(report.id)}
                target="_blank"
                rel="noreferrer"
                data-testid="view-pdf-link"
                className="inline-flex items-center gap-1 text-sm text-primary min-h-[44px]"
              >
                <FileText className="h-4 w-4" /> View PDF
              </a>
            )
          }
        />
      </div>

      {(actionError || compileError) && (
        <div className="mx-[var(--page-padding-x)] mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="alert" data-testid="action-error">
          <p className="text-destructive">{actionError ?? compileError}</p>
        </div>
      )}

      {generateGaps && generateGaps.length > 0 && (
        <div className="mx-[var(--page-padding-x)] mb-4 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm" role="alert" data-testid="generate-gaps">
          <p className="font-medium mb-2 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Not ready to generate:</p>
          <ul className="list-disc pl-5">
            {generateGaps.map((gap, i) => <li key={i}>{gap.message}</li>)}
          </ul>
        </div>
      )}

      {generateWarnings && generateWarnings.length > 0 && (
        <div className="mx-[var(--page-padding-x)] mb-4 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm" data-testid="generate-warnings">
          <p className="font-medium mb-2">Generated with warnings:</p>
          <ul className="list-disc pl-5">
            {generateWarnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* Line item buckets */}
      <div className="px-[var(--page-padding-x)] flex flex-col gap-6">
        {matchedItems.length === 0 && unverifiedItems.length === 0 && (
          <div
            className="rounded-lg border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground text-center"
            data-testid="no-deductions-notice"
          >
            No deductions found — this move-out didn't turn up any deficient or unverified items.
          </div>
        )}
        <LineItemBucket
          title="Matched to Move-In"
          items={matchedItems}
          drafts={lineDrafts}
          setDrafts={setLineDrafts}
          onSave={handleSaveLine}
          savingLineId={savingLineId}
          reportId={report.id}
          editable={isDraftOrGenerated}
        />
        <LineItemBucket
          title="Unverified Candidates"
          description="No matching move-in baseline item was found — review before charging."
          items={unverifiedItems}
          drafts={lineDrafts}
          setDrafts={setLineDrafts}
          onSave={handleSaveLine}
          savingLineId={savingLineId}
          reportId={report.id}
          editable={isDraftOrGenerated}
        />
      </div>

      {/* Running total + actions */}
      <div className="px-[var(--page-padding-x)] mt-6 flex flex-col gap-3">
        <div className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3" data-testid="running-total">
          <span className="text-sm font-medium text-foreground">Running Total</span>
          <span className="text-lg font-semibold text-foreground">{formatAmount(report.totalCents)}</span>
        </div>

        {report.status !== 'SENT' && (
          <Button variant="outline" onClick={handleCompile} disabled={isCompiling} data-testid="compile-report-button">
            {isCompiling ? 'Recompiling…' : 'Recompile Report'}
          </Button>
        )}

        {report.status !== 'SENT' && (
          <Button onClick={handleGenerate} disabled={isGenerating} data-testid="generate-report-button">
            {isGenerating ? 'Generating…' : report.status === 'GENERATED' ? 'Regenerate Report' : 'Generate Report'}
          </Button>
        )}

        {report.status === 'GENERATED' && (
          <Button onClick={handleSend} disabled={isSending} data-testid="send-report-button">
            <Send className="h-4 w-4 mr-1" /> {isSending ? 'Sending…' : 'Send to Tenant'}
          </Button>
        )}

        {report.status === 'SENT' && (
          <div className="rounded-lg border border-border bg-card px-4 py-3 text-sm" data-testid="delivery-status-panel">
            <p>
              Sent {report.sentAt ? new Date(report.sentAt).toLocaleString() : ''}
              {report.deliveryEmail && ` to ${report.deliveryEmail}`} — <DeliveryStatusInline status={report.deliveryStatus} />
            </p>
            {report.deliveryStatus === 'FAILED' && (
              <Button variant="outline" size="sm" onClick={handleRetry} disabled={isRetrying} className="mt-2" data-testid="retry-delivery-button">
                <RotateCw className="h-4 w-4 mr-1" /> {isRetrying ? 'Retrying…' : 'Retry Delivery'}
              </Button>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              Tenant acknowledgment — move-in: {report.acknowledgment.landlordMoveInAckedByTenant ? 'Acknowledged' : 'Pending'},
              {' '}move-out: {report.acknowledgment.landlordMoveOutAckedByTenant ? 'Acknowledged' : 'Pending'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

interface LineItemBucketProps {
  title: string;
  description?: string;
  items: DeductionLineItemResponse[];
  drafts: Record<string, { amount: string; description: string }>;
  setDrafts: React.Dispatch<React.SetStateAction<Record<string, { amount: string; description: string }>>>;
  onSave: (lineItemId: string) => void;
  savingLineId: string | null;
  reportId: string;
  editable: boolean;
}

function LineItemBucket({ title, description, items, drafts, setDrafts, onSave, savingLineId, reportId, editable }: LineItemBucketProps) {
  if (items.length === 0) return null;
  return (
    <div data-testid={`line-item-bucket-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <h2 className="text-sm font-semibold text-foreground mb-1">{title}</h2>
      {description && <p className="text-xs text-muted-foreground mb-2">{description}</p>}
      <div className="flex flex-col gap-3">
        {items.map((li) => {
          const draft = drafts[li.id] ?? { amount: (li.amountCents / 100).toFixed(2), description: li.description };
          return (
            <div key={li.id} className="rounded-lg border border-border bg-card p-3" data-testid={`line-item-${li.id}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-sm font-medium text-foreground">{li.room} · {li.item}</p>
                <Badge variant="outline" className="text-xs">{li.baselineStatus === 'MATCHED' ? 'Matched' : 'Unverified'}</Badge>
              </div>

              {/* Side-by-side move-in / move-out / tenant counter-evidence */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3 text-xs">
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="font-medium text-foreground mb-1">Move-In</p>
                  <p><ConditionLabel condition={li.moveIn.condition} /></p>
                  {/* BR-13: a zero-photo baseline shows the explicit marker (mirrors
                      deduction-report.service.ts's renderReportMarkdown()) rather than a bare
                      "0 photo(s)" that reads like a broken/empty count. */}
                  <p className="text-muted-foreground" data-testid={`line-item-${li.id}-move-in-evidence`}>
                    {li.moveIn.photoCount > 0
                      ? `${li.moveIn.photoCount} photo${li.moveIn.photoCount === 1 ? '' : 's'}`
                      : 'no baseline photo on record'}
                  </p>
                </div>
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="font-medium text-foreground mb-1">Move-Out</p>
                  <p><ConditionLabel condition={li.moveOut.condition} /></p>
                  {li.moveOut.deficiencyTypes.length > 0 && (
                    <p className="text-muted-foreground">{li.moveOut.deficiencyTypes.map((t) => DEFICIENCY_TYPE_LABELS[t]).join(', ')}</p>
                  )}
                  {/* BR-13: a zero-photo move-out mirrors the move-in marker above (and
                      deduction-report.service.ts's renderReportMarkdown()) rather than a
                      bare "0 photo(s)" that reads like a broken/empty count. */}
                  <p className="text-muted-foreground" data-testid={`line-item-${li.id}-move-out-evidence`}>
                    {li.moveOut.photoCount > 0
                      ? `${li.moveOut.photoCount} photo${li.moveOut.photoCount === 1 ? '' : 's'}`
                      : 'no move-out photo on record'}
                  </p>
                </div>
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="font-medium text-foreground mb-1">Tenant Counter-Evidence</p>
                  {li.tenantMoveOut ? (
                    <>
                      <p><ConditionLabel condition={li.tenantMoveOut.condition} /></p>
                      <p className="text-muted-foreground">{li.tenantMoveOut.photoCount} photo(s)</p>
                    </>
                  ) : (
                    <p className="text-muted-foreground">None submitted</p>
                  )}
                </div>
              </div>

              {li.moveOut.narrative && <p className="text-xs text-muted-foreground mb-3 italic">"{li.moveOut.narrative}"</p>}

              {/* Amount + description + receipt */}
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-2 items-end">
                <div>
                  <label htmlFor={`line-desc-${li.id}`} className="block mb-1 text-xs font-medium text-foreground">Description</label>
                  <Input
                    id={`line-desc-${li.id}`}
                    data-testid={`line-description-input-${li.id}`}
                    type="text"
                    className="px-2.5 py-2 border-border box-border min-h-[44px]"
                    value={draft.description}
                    disabled={!editable}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [li.id]: { ...draft, description: e.target.value } }))}
                  />
                </div>
                <div>
                  <label htmlFor={`line-amount-${li.id}`} className="block mb-1 text-xs font-medium text-foreground">Amount</label>
                  <Input
                    id={`line-amount-${li.id}`}
                    data-testid={`line-amount-input-${li.id}`}
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    className="px-2.5 py-2 border-border box-border min-h-[44px]"
                    value={draft.amount}
                    disabled={!editable}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [li.id]: { ...draft, amount: e.target.value } }))}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 mt-2">
                {li.receiptStorageKey ? (
                  <a
                    href={deductionLineItemReceiptUrl(reportId, li.id)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`line-receipt-link-${li.id}`}
                    className="inline-flex items-center gap-1 text-xs text-primary min-h-[44px]"
                  >
                    <Paperclip className="h-3.5 w-3.5" /> View receipt
                  </a>
                ) : (
                  <span className="text-xs text-muted-foreground">No receipt attached</span>
                )}
                {editable && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onSave(li.id)}
                    disabled={savingLineId === li.id}
                    data-testid={`line-save-button-${li.id}`}
                  >
                    {savingLineId === li.id ? 'Saving…' : 'Save'}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default DeductionReportPage;
